import React, { useState } from 'react';
import { Button, Input, Select, Checkbox, Collapse } from 'antd';
import { PlusOutlined, DeleteOutlined, DownOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const ContextGroupList = ({ value = [], onChange, groupSchema = [] }) => {
  const [groups, setGroups] = useState(Array.isArray(value) ? value : []);
  const [activeKeys, setActiveKeys] = useState(['0']); // Keep first group expanded by default
  
  // Debug logging
  console.log('ContextGroupList rendered:', { 
    value, 
    groupsCount: groups.length, 
    groupSchemaCount: groupSchema.length,
    groupSchema 
  });
  
  // Update groups when value prop changes
  React.useEffect(() => {
    if (Array.isArray(value)) {
      setGroups(value);
    }
  }, [value]);

  const handleGroupChange = (index, field, fieldValue) => {
    const newGroups = [...groups];
    newGroups[index] = {
      ...newGroups[index],
      [field]: fieldValue
    };
    setGroups(newGroups);
    if (onChange) {
      onChange(newGroups);
    }
  };

  const addGroup = () => {
    const newGroup = {};
    // Initialize with default values from schema
    groupSchema.forEach(field => {
      newGroup[field.name] = field.default_value !== undefined ? field.default_value : '';
    });
    const newGroups = [...groups, newGroup];
    setGroups(newGroups);
    // Expand the newly added group
    const newIndex = String(groups.length);
    setActiveKeys([...activeKeys.filter(k => k !== newIndex), newIndex]);
    if (onChange) {
      onChange(newGroups);
    }
  };

  const removeGroup = (index) => {
    const newGroups = groups.filter((_, i) => i !== index);
    setGroups(newGroups);
    // Remove from active keys
    setActiveKeys(activeKeys.filter(key => parseInt(key) !== index));
    if (onChange) {
      onChange(newGroups);
    }
  };

  const shouldShowField = (field, groupData) => {
    if (!field.conditional) return true;
    
    const condField = field.conditional.field;
    const condValue = field.conditional.value;
    const condOperator = field.conditional.operator || 'equals';
    
    const actualValue = groupData[condField];
    
    if (condOperator === 'equals') {
      return actualValue === condValue;
    }
    
    return true;
  };

  const renderField = (field, groupIndex, groupData) => {
    if (!shouldShowField(field, groupData)) {
      return null;
    }

    const fieldValue = groupData[field.name] !== undefined ? groupData[field.name] : field.default_value;

    switch (field.field_type) {
      case 'boolean':
        return (
          <div style={{ marginBottom: 12 }} key={field.name}>
            <Checkbox
              checked={fieldValue === true || fieldValue === 'true'}
              onChange={(e) => handleGroupChange(groupIndex, field.name, e.target.checked)}
            >
              {field.label}
            </Checkbox>
            {field.help_text && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                {field.help_text}
              </div>
            )}
          </div>
        );

      case 'select':
        return (
          <div style={{ marginBottom: 12 }} key={field.name}>
            <div style={{ marginBottom: 4, fontSize: '13px', fontWeight: 500 }}>
              {field.label}
              {field.required && <span style={{ color: 'red' }}> *</span>}
            </div>
            <Select
              value={fieldValue}
              onChange={(val) => handleGroupChange(groupIndex, field.name, val)}
              style={{ width: '100%' }}
              placeholder={field.placeholder || `Select ${field.label}`}
            >
              {field.options && typeof field.options === 'object' && Object.entries(field.options).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
            {field.help_text && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                {field.help_text}
              </div>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div style={{ marginBottom: 12 }} key={field.name}>
            <div style={{ marginBottom: 4, fontSize: '13px', fontWeight: 500 }}>
              {field.label}
              {field.required && <span style={{ color: 'red' }}> *</span>}
            </div>
            <Select
              mode="multiple"
              value={fieldValue || []}
              onChange={(val) => handleGroupChange(groupIndex, field.name, val)}
              style={{ width: '100%' }}
              placeholder={field.placeholder || `Select ${field.label}`}
            >
              {field.options && typeof field.options === 'object' && Object.entries(field.options).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
            {field.help_text && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                {field.help_text}
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div style={{ marginBottom: 12 }} key={field.name}>
            <div style={{ marginBottom: 4, fontSize: '13px', fontWeight: 500 }}>
              {field.label}
              {field.required && <span style={{ color: 'red' }}> *</span>}
            </div>
            <TextArea
              value={fieldValue}
              onChange={(e) => handleGroupChange(groupIndex, field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label}`}
              rows={3}
            />
            {field.help_text && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                {field.help_text}
              </div>
            )}
          </div>
        );

      case 'string':
      case 'number':
      case 'integer':
      default:
        return (
          <div style={{ marginBottom: 12 }} key={field.name}>
            <div style={{ marginBottom: 4, fontSize: '13px', fontWeight: 500 }}>
              {field.label}
              {field.required && <span style={{ color: 'red' }}> *</span>}
            </div>
            <Input
              type={field.field_type === 'number' || field.field_type === 'integer' ? 'number' : 'text'}
              value={fieldValue}
              onChange={(e) => handleGroupChange(groupIndex, field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label}`}
            />
            {field.help_text && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                {field.help_text}
              </div>
            )}
          </div>
        );
    }
  };

  const getGroupTitle = (group, index) => {
    const mode = group.operation_mode || 'set_context';
    const key = group.context_key || group.regex_target_key || '';
    const enabled = group.enabled !== false;
    const conditional = group.condition_enabled === true;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <span style={{ fontWeight: 'bold' }}>#{index + 1}</span>
        <span style={{ flex: 1 }}>
          {mode === 'set_context' ? '📝 Set' : '🔄 Transform'}: {key || '(not set)'}
        </span>
        {conditional && <span style={{ color: '#1890ff', fontSize: '11px' }}>🔀 Conditional</span>}
        {!enabled && <span style={{ color: '#999', fontSize: '11px' }}>⏸ Disabled</span>}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button
          type="dashed"
          onClick={addGroup}
          icon={<PlusOutlined />}
          block
        >
          Add Context Operation
        </Button>
      </div>

      {groups.length === 0 && (
        <div style={{ 
          padding: 24, 
          textAlign: 'center', 
          color: '#999',
          border: '1px dashed #d9d9d9',
          borderRadius: 4
        }}>
          No context operations defined. Click "Add Context Operation" to get started.
        </div>
      )}

      <Collapse 
        activeKey={activeKeys}
        onChange={setActiveKeys}
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      >
        {groups.map((group, index) => (
          <Panel
            header={getGroupTitle(group, index)}
            key={String(index)}
            extra={
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  removeGroup(index);
                }}
              />
            }
          >
            <div style={{ padding: '8px 0' }}>
              {console.log(`Rendering fields for group ${index}:`, {
                groupSchemaLength: groupSchema.length,
                groupData: group,
                sortedSchema: groupSchema.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
              })}
              {groupSchema
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map(field => {
                  console.log(`Rendering field ${field.name}:`, {
                    shouldShow: shouldShowField(field, group),
                    field
                  });
                  return renderField(field, index, group);
                })}
            </div>
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

export default ContextGroupList;
